import { differenceInMilliseconds } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";

import { convertServerTimeToLocal } from "@whoosh/utilities/src/dates";

type Props = {
  lockedUntil: string | null;
  onExpire: () => void;
  serverTimeOffsetMs?: number | null;
};
export const useSlotLockCountdownTimerData = ({
  lockedUntil,
  onExpire,
  serverTimeOffsetMs = 0,
}: Props) => {
  const expiresAt = useMemo(
    () => (lockedUntil ? convertServerTimeToLocal(lockedUntil) : null),
    [lockedUntil]
  );
  const initialTimeDisplay = calculateTimeDisplay(
    expiresAt,
    serverTimeOffsetMs
  );
  const initialTime = expiresAt
    ? `${initialTimeDisplay.minutes}:${initialTimeDisplay.displaySeconds}`
    : null;
  const [timeLow, setTimeLow] = useState(initialTimeDisplay.timeLow);
  const [timeWarning, setTimeWarning] = useState<string | null>(initialTime);
  const expiredLockRef = useRef<string | null>(null);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (!expiresAt || !lockedUntil) {
      expiredLockRef.current = null;
      setTimeWarning(null);
      setTimeLow(false);
      return;
    }

    const updateTimer = () => {
      const timeDisplay = calculateTimeDisplay(expiresAt, serverTimeOffsetMs);

      setTimeLow(timeDisplay.timeLow);
      setTimeWarning(`${timeDisplay.minutes}:${timeDisplay.displaySeconds}`);

      if (timeDisplay.isExpired) {
        if (expiredLockRef.current !== lockedUntil) {
          expiredLockRef.current = lockedUntil;
          onExpireRef.current();
        }
        return false;
      }

      return true;
    };

    if (!updateTimer()) return;

    const interval = setInterval(() => {
      if (!updateTimer()) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, lockedUntil, serverTimeOffsetMs]);

  return {
    timeWarning,
    timeLow,
  };
};

type TimeDisplay = {
  minutes: number;
  displaySeconds: string;
  isExpired: boolean;
  timeLow: boolean;
};

const calculateTimeDisplay = (
  expiresAt: Date | null,
  serverTimeOffsetMs: number | null
): TimeDisplay => {
  const now = Date.now() + (serverTimeOffsetMs ?? 0);
  const milliseconds = differenceInMilliseconds(expiresAt || now, now);
  const remainingMilliseconds = Math.max(milliseconds, 0);
  const minutes = Math.floor(remainingMilliseconds / 60_000);
  const seconds = Math.floor((remainingMilliseconds % 60_000) / 1000);
  const displaySeconds = seconds.toString().padStart(2, "0");

  return {
    minutes,
    displaySeconds,
    isExpired: milliseconds <= 0,
    timeLow: remainingMilliseconds < 2 * 60_000,
  };
};
